import React, { useState } from "react";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import axios from "config/axios";
import { toast } from "react-toastify";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const CourseMap = ({ course, courseId, fetchCourseDetails }) => {
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [fileName, setFileName] = useState("");
  const [file, setFile] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setFileName(selectedFile?.name || "");
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file to upload");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/courseMap/courses/${courseId}/upload-course-map`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.status === 200) {
        toast.success("Course map uploaded successfully");
        setMapModalOpen(false);
        setFile(null);
        setFileName("");
        // Refresh course details in parent
        await fetchCourseDetails?.();
      }
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("Failed to upload course map");
    }
  };

  const handleView = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/courseMap/courses/${courseId}/course-map`;
    window.open(url, "_blank");
  };

  const handleDelete = async () => {
    // const confirm = window.confirm(
    //   "Are you sure you want to delete this course map?"
    // );
    if (!confirm) return;

    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/courseMap/courses/${courseId}/delete-course-map`
      );

      if (response.status === 200) {
        toast.success("Course map deleted successfully");
        await fetchCourseDetails(); // refresh course data
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete course map");
    }
  };

  return (
    <div className="rounded overflow-hidden shadow-lg w-4/5 mt-5">
      <div className="px-6 py-4 flex md:flex-row flex-col items-center justify-between border-b">
        <div className="flex flex-col items-start justify-center w-full">
          {course && (
            <div className="font-bold text-lg sm:text-xl mb-2 w-full break-words whitespace-normal">
              <span className="block sm:inline">PDF: </span>
              <span className="text-gray-800 break-all">{course.title}</span>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 md:flex-row w-full items-center justify-end mt-3 md:mt-0">
          {/* Upload / Update Button */}
          <button
            className="bg-blue-500 hover:bg-blue-400 text-white font-bold py-2 px-4 border-b-4 border-blue-700 hover:border-blue-500 rounded mr-2"
            onClick={() => setMapModalOpen(true)}
          >
            {course?.courseMapFileId ? "Update" : "Upload"}
          </button>

          {/* View Button - only show if course map exists */}
          {course?.courseMapFileId && (
            <button
              className="bg-green-500 hover:bg-green-400 text-white font-bold py-2 px-4 border-b-4 border-green-700 hover:border-green-500 rounded mr-2"
              onClick={() => handleView()}
            >
              View
            </button>
          )}

          {/* Delete */}
          {course?.courseMapFileId && (
            <div className="flex items-center gap-2">
              {!confirmDelete ? (
                <button
                  className="bg-red-500 hover:bg-red-400 text-white font-bold py-2 px-4 border-b-4 border-red-700 hover:border-red-500 rounded transition"
                  onClick={() => setConfirmDelete(true)}
                >
                  Delete
                </button>
              ) : (
                <>
                  <button
                    className="bg-red-500 hover:bg-red-400 text-white font-bold py-2 px-4 border-b-4 border-red-700 hover:border-red-500 rounded transition"
                    onClick={() => {
                      handleDelete();
                      setConfirmDelete(false);
                    }}
                  >
                    Confirm
                  </button>
                  <button
                    className="bg-gray-500 hover:bg-gray-400 text-white font-bold py-2 px-4 border-b-4 border-gray-700 hover:border-gray-500 rounded transition"
                    onClick={() => setConfirmDelete(false)}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          )}

          {/* Modal */}
          <Modal
            open={mapModalOpen}
            onClose={() => setMapModalOpen(false)}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            className="rounded-lg"
          >
            <Box sx={style}>
              <Typography id="modal-modal-title" variant="h6" component="h2">
                {course?.courseMapFileId ? "Update" : "Upload"}
              </Typography>

              <TextField
                className="w-full mt-3"
                type="file"
                onChange={handleFileChange}
              />

              <div className="flex w-full justify-end items-center mt-3">
                <Button
                  style={{ marginRight: "5px" }}
                  variant="contained"
                  onClick={() => handleUpload()}
                >
                  Upload
                </Button>
                <Button
                  style={{ marginLeft: "5px" }}
                  variant="contained"
                  onClick={() => setMapModalOpen(false)}
                >
                  Close
                </Button>
              </div>
            </Box>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default CourseMap;
